import {
  setLegends,
  // setSelectedBuildingId,
  setSelectedLotId,
} from 'features/siteMapSlice';
import { GOOGLE_MAP_PIXEL_WIDTH, LAT_DEGREE } from 'lib/constants';
import {
  IFilterDictionary,
  IFilterObject,
  ILegend,
  ILotInfo,
  ISelectedFilters,
  ISiteMapData,
  ISmGroundOverlay,
} from 'lib/models/siteMap.interface';
import { fadeIn, fadeOut } from 'lib/utils/animations';
import { Dispatch } from 'redux';

export const initiateSVG = ({
  siteMapData,
  showDetails,
  showDimensions,
  dispatch,
  gaFunction,
  setIsInitiated,
}: {
  siteMapData: ISiteMapData;
  showDetails: boolean;
  showDimensions: boolean;
  dispatch: Dispatch;
  gaFunction: any;
  setIsInitiated: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const sitePlanSvg = document.querySelector('#site-map-svg > svg') as SVGGraphicsElement;
  const displayOption = siteMapData.displayOptions[0];

  if (!showDetails) {
    hideDetails();
  }
  if (!showDimensions) {
    hideDimensions();
  }

  // change lot color to status color and set legend
  const legend: ILegend = {};

  Object.values(siteMapData.lots).forEach((lot) => {
    const { label, color } = siteMapData.status[lot.comStatusSysId];
    (sitePlanSvg.querySelector(`#${lot.instanceName} path`)! as HTMLElement).style.fill = color;
    legend[label] = color;
  });
  if (displayOption.showAllAvailableLegend) {
    Object.values(siteMapData.status).forEach(({ label, color }) => {
      legend[label] = color;
    });
  }
  dispatch(setLegends(legend));

  // add event listeners for lots
  const { lotPrefix, tractPrefix, buildingPrefix } = displayOption;
  sitePlanSvg.querySelectorAll('.sp-inv-item').forEach((domLot) => {
    domLot.addEventListener('click', () => {
      dispatch(setSelectedLotId(domLot.id));
      // GA
      const lotTitle = getLotTitle({
        instanceName: domLot.id,
        lotPrefix,
        tractPrefix,
        buildingPrefix,
      });
      gaFunction.spSelectLot(lotTitle, domLot.id);
    });
    domLot.addEventListener('mouseover', () => {
      domLot.classList.add('hovered-lot');
    });
    domLot.addEventListener('mouseout', () => {
      domLot.classList.remove('hovered-lot');
    });
  });

  // TODO: check with Jeet/Darren
  // add event listeners for buildings
  sitePlanSvg.querySelectorAll('[id^=building]').forEach((domLot) => {
    domLot.addEventListener('click', () => {
      // dispatch(setSelectedBuildingId(domLot.id));
    });
    domLot.addEventListener('mouseover', () => {
      domLot.classList.add('hovered-building');
    });
    domLot.addEventListener('mouseout', () => {
      domLot.classList.remove('hovered-building');
    });
  });

  setIsInitiated(true);
};

export const getFilterDictionary = (siteMapData: ISiteMapData) => {
  const filterDictionary: IFilterDictionary = siteMapData.stdFilters.reduce((acc, filter) => {
    if (filter.showOnWeb) {
      return {
        ...acc,
        [filter.name]: {
          title: filter.title,
          filters: [],
        },
      };
    }

    return acc;
  }, {});

  // phase filter
  if (filterDictionary.phase) {
    Object.values(siteMapData.phases).forEach(({ sysId, name, orderBy }) => {
      const lots: string[] = [];
      Object.entries(siteMapData.lots).forEach(([key, value]) => {
        if (value.comPhasesSysId === sysId) {
          lots.push(key);
        }
      });
      if (lots.length) {
        filterDictionary.phase.filters.push({
          sysId,
          name,
          filtered: lots,
          orderBy,
        });
      }
    });
  }

  // status filter
  if (filterDictionary.status) {
    Object.values(siteMapData.status).forEach(({ sysId, label, orderBy }) => {
      const lots: string[] = [];
      Object.entries(siteMapData.lots).forEach(([key, value]) => {
        if (value.comStatusSysId === sysId) {
          lots.push(key);
        }
      });
      if (lots.length) {
        filterDictionary.status.filters.push({
          sysId,
          name: label,
          filtered: lots,
          orderBy,
        });
      }
    });
  }

  // plan name filter
  if (filterDictionary.planName) {
    Object.values(siteMapData.plans).forEach(({ sysId, name, menuOrder }) => {
      const lots: string[] = [];
      Object.entries(siteMapData.lots).forEach(([key, value]) => {
        if (value.planElvs.find((planElv) => planElv.comPlansSysId === sysId)) {
          lots.push(key);
        }
      });
      filterDictionary.planName.filters.push({
        sysId,
        name,
        filtered: lots,
        orderBy: menuOrder,
      });
    });
  }

  // elevation filter
  if (filterDictionary.elevation) {
    let filterObject: {
      [elevationName: string]: {
        sysId: string;
        name: string;
        filtered: string[];
        orderBy: number;
      };
    } = {};
    Object.values(siteMapData.elevations).forEach(({ sysId, style, orderBy }) => {
      const lots: string[] = [];
      Object.entries(siteMapData.lots).forEach(([key, value]) => {
        if (value.planElvs.find((planElv) => planElv.comElevationsSysId === sysId)) {
          lots.push(key);
        }
      });
      if (filterObject[style]) {
        filterObject[style].filtered = [...filterObject[style].filtered, ...lots];
      } else {
        filterObject[style] = {
          sysId,
          name: style,
          filtered: lots,
          orderBy,
        };
      }
    });
    filterDictionary.elevation.filters = [...Object.values(filterObject)];
  }

  /*************************** extended filters ****************************/
  if (siteMapData.extFilters.length) {
    siteMapData.extFilters.forEach((extFilter) => {
      filterDictionary[extFilter.title] = {
        title: extFilter.title,
        filters: [],
      };

      //object
      if (extFilter.filterType === 'object') {
        // parse the metaJson and loop through filter objects
        const filterObjects: IFilterObject[] = JSON.parse(extFilter.metadataJson);
        filterObjects.forEach(({ key, label }, i) => {
          // check the applied column in lot data that has same key as filter obj
          const lots: string[] = [];
          Object.values(siteMapData.lots).forEach((lot) => {
            const columnValue = lot[extFilter.applyOnColumns as keyof ILotInfo] as string;
            if (columnValue?.includes(key)) {
              // if has the same key push to filtered lot for current ext filter
              lots.push(lot.instanceName);
            }
          });

          // add the filter if there are available lots for current ext filter
          if (lots.length) {
            filterDictionary[extFilter.title].filters.push({
              sysId: key,
              name: label,
              filtered: lots,
              orderBy: i + 1,
            });
          }
        });

        // range
      } else if (extFilter.filterType === 'range') {
        let filters: {
          sysId: string;
          name: string;
          filtered: string[];
        }[] = [];

        const columns = extFilter.applyOnColumns.split(',');
        const rangeLots: {
          [range: string]: {
            minRange: number;
            maxRange: number;
            lots: string[];
          };
        } = {};
        let minValue: number | undefined;
        let maxValue: number | undefined;

        // absolute value for the range (ex: parking, bedrooms, bathrooms)
        if (extFilter.step === 0) {
          Object.values(siteMapData.lots).forEach((lot) => {
            let minData = lot[columns[0] as keyof ILotInfo];
            let maxData = lot[columns[1] as keyof ILotInfo];
            if (minData && maxData) {
              const lotMin = Number(minData);
              const lotMax = Number(maxData);
              for (let i = lotMin; i <= lotMax; i++) {
                if (rangeLots[i]) {
                  rangeLots[i].lots.push(lot.instanceName);
                } else {
                  rangeLots[i] = {
                    minRange: 0,
                    maxRange: 0,
                    lots: [lot.instanceName],
                  };
                }
              }
            }
          });
        } else {
          // get total min and total max value
          Object.values(siteMapData.lots).forEach((lot) => {
            let minData = lot[columns[0] as keyof ILotInfo];
            let maxData = lot[columns[1] as keyof ILotInfo];
            if (minData && maxData) {
              const lotMin = Number(minData);
              const lotMax = Number(maxData);
              if (minValue) {
                minValue = lotMin < minValue ? lotMin : minValue;
              } else {
                minValue = lotMin;
              }

              if (maxValue) {
                maxValue = lotMax > maxValue ? lotMax : maxValue;
              } else {
                maxValue = lotMax;
              }
            }
          });

          if (minValue && maxValue) {
            // collect min and max and range name
            let i = minValue;
            do {
              const minRange = i;
              const maxRange = i + extFilter.step - 1;
              const range = `${extFilter.prefix || ''} ${minRange} - ${maxRange} ${
                extFilter.suffix || ''
              }`;
              rangeLots[range] = {
                minRange,
                maxRange,
                lots: [],
              };
              i += extFilter.step;
            } while (i <= maxValue + extFilter.step);

            // collect min and max and range name
            Object.values(siteMapData.lots).forEach((lot) => {
              let minData = lot[columns[0] as keyof ILotInfo];
              let maxData = lot[columns[1] as keyof ILotInfo];
              if (minData && maxData) {
                const lotMin = Number(minData);
                const lotMax = Number(maxData);
                const lotRange: number[] = [lotMin, lotMax];
                let lotRangeIndexValue = lotMin + extFilter.step;
                while (lotRangeIndexValue <= lotMax) {
                  lotRange.push(lotRangeIndexValue);
                  lotRangeIndexValue += extFilter.step;
                }
                Object.entries(rangeLots).forEach(([range, rangeLot]) => {
                  // regular range (ex: price, sq.ft)
                  if (
                    lotRange.filter(
                      (lotRangeValue) =>
                        lotRangeValue >= rangeLot.minRange && lotRangeValue <= rangeLot.maxRange
                    ).length
                  ) {
                    rangeLots[range].lots.push(lot.instanceName);
                  }
                });
              }
            });
          }
        }
        Object.entries(rangeLots).forEach(([range, rangeLot]) => {
          if (rangeLot.lots.length) {
            filters.push({
              sysId: range,
              name: range,
              filtered: [...rangeLot.lots],
            });
          }
        });

        filterDictionary[extFilter.title].filters = filters.map((filter, i) => ({
          ...filter,
          orderBy: i + 1,
        }));

        // value
      } else {
        let filters: {
          sysId: string;
          name: string;
          filtered: string[];
        }[] = [];

        const rangeLots: {
          [range: string]: {
            minRange: number;
            maxRange: number;
            lots: string[];
          };
        } = {};
        let minValue: number | undefined;
        let maxValue: number | undefined;

        // absolute value for the range (ex: parking, bedrooms, bathrooms)
        if (extFilter.step === 0) {
          Object.values(siteMapData.lots).forEach((lot) => {
            const value = lot[extFilter.applyOnColumns as keyof ILotInfo];
            if (value) {
              const filterValue = Number(value);
              if (rangeLots[filterValue]) {
                rangeLots[filterValue].lots.push(lot.instanceName);
              } else {
                rangeLots[filterValue] = {
                  minRange: 0,
                  maxRange: 0,
                  lots: [lot.instanceName],
                };
              }
            }
          });
        } else {
          // get total min and total max value
          Object.values(siteMapData.lots).forEach((lot) => {
            const value = lot[extFilter.applyOnColumns as keyof ILotInfo];
            if (value) {
              const filterValue = Number(value);
              if (minValue) {
                minValue = filterValue < minValue ? filterValue : minValue;
              } else {
                minValue = filterValue;
              }

              if (maxValue) {
                maxValue = filterValue > maxValue ? filterValue : maxValue;
              } else {
                maxValue = filterValue;
              }
            }
          });

          if (minValue && maxValue) {
            // collect min and max and range name
            let i = minValue;
            do {
              const minRange = i;
              const maxRange = i + extFilter.step - 1;
              const range = `${extFilter.prefix || ''} ${minRange} - ${maxRange} ${
                extFilter.suffix || ''
              }`;
              rangeLots[range] = {
                minRange,
                maxRange,
                lots: [],
              };
              i += extFilter.step;
            } while (i <= maxValue);

            // loop lots and push to correct range
            Object.values(siteMapData.lots).forEach((lot) => {
              let value = lot[extFilter.applyOnColumns as keyof ILotInfo];
              if (value) {
                const filterValue = Number(value);
                // regular range (ex: price, sq.ft)
                Object.entries(rangeLots).forEach(([range, rangeLot]) => {
                  if (filterValue >= rangeLot.minRange && filterValue <= rangeLot.maxRange) {
                    rangeLots[range].lots.push(lot.instanceName);
                  }
                });
              }
            });
          }
        }

        Object.entries(rangeLots).forEach(([range, rangeLot]) => {
          if (rangeLot.lots.length) {
            filters.push({
              sysId: range,
              name: range,
              filtered: [...rangeLot.lots],
            });
          }
        });

        filterDictionary[extFilter.title].filters = filters.map((filter, i) => ({
          ...filter,
          orderBy: i + 1,
        }));
      }
    });
  }

  // remove empty filters from dictionary
  Object.entries(filterDictionary).forEach(([filterName, value]) => {
    if (!value.filters.length) {
      delete filterDictionary[filterName];
    } else {
      value.filters.sort((a, b) => a.orderBy - b.orderBy);
    }
  });

  return filterDictionary;
};

export const showAndGetFilteredLots = ({
  filterDictionary,
  selectedFilters,
  siteMapData,
}: {
  filterDictionary: IFilterDictionary;
  selectedFilters: ISelectedFilters;
  siteMapData: ISiteMapData;
}) => {
  let filteredResult: string[] = [];

  Object.entries(selectedFilters).forEach(([filterName, selectedFilterSysIds]) => {
    if (selectedFilterSysIds.length) {
      let filtered = filterDictionary[filterName].filters.reduce((acc: string[], filter) => {
        if (selectedFilterSysIds.includes(filter.sysId)) {
          return [...acc, ...filter.filtered];
        }
        return [...acc];
      }, []);

      // remove duplicated lots
      filtered = [...new Set(filtered)];

      // trim the same lots from previous filtered lots
      filteredResult = filteredResult.length
        ? filteredResult.filter((result) => filtered.includes(result))
        : [...filtered];
    }
  });

  // turn all other lots to gray
  Object.values(siteMapData.lots).forEach((lot) => {
    if (filteredResult!.includes(lot.instanceName)) {
      const { color } = siteMapData.status[lot.comStatusSysId];
      document.querySelector(`#${lot.instanceName} path`)?.setAttribute('style', 'fill: ' + color);
    } else {
      document.querySelector(`#${lot.instanceName} path`)?.setAttribute('style', 'display: none');
    }
  });

  return filteredResult;
};

export const resetFilterSVG = (siteMapData: ISiteMapData) => {
  // turn all other lots to gray
  Object.values(siteMapData.lots).forEach((lot) => {
    const { color } = siteMapData.status[lot.comStatusSysId];
    document.querySelector(`#${lot.instanceName} path`)?.setAttribute('style', 'fill: ' + color);
  });
};

export const displayDetails = () => {
  const domDetail = document.querySelector('.sp-foot-prints')!;
  domDetail.classList.remove('hidden');
  fadeIn(domDetail);
};

export const hideDetails = () => {
  const domDetail = document.querySelector('.sp-foot-prints')!;
  fadeOut(domDetail);
};

export const displayDimensions = () => {
  const domDims = document.getElementById('dims')!;
  domDims.classList.remove('hidden');
  fadeIn(domDims);
};

export const hideDimensions = () => {
  const domDims = document.getElementById('dims')!;
  fadeOut(domDims);
};

export const hideNavBar = () => {
  const nav = document.querySelector('[data-cy=nav-bar]');
  nav?.classList.add('hidden');
};

export const showNavBar = () => {
  const nav = document.querySelector('[data-cy=nav-bar]');
  nav?.classList.remove('hidden');
};

export const getLotTitle = ({
  instanceName,
  lotPrefix,
  tractPrefix,
  buildingPrefix,
}: {
  instanceName: string;
  lotPrefix?: string;
  tractPrefix?: string;
  buildingPrefix?: string;
}) => {
  const lotSplitArray = instanceName.split('_');
  let lotTitle = '';
  const lotName =
    (lotPrefix || lotSplitArray[0]).charAt(0).toUpperCase() +
    (lotPrefix || lotSplitArray[0]).slice(1);
  const lotNumber = lotSplitArray.length === 2 ? lotSplitArray[1] : lotSplitArray[2];
  lotTitle = lotName + ' ' + lotNumber;

  // if the instance name has building or track
  if (lotSplitArray.length === 3) {
    // lot & tract
    if (lotSplitArray[0] === 'lot') {
      lotTitle = (tractPrefix || 'Tract') + ' ' + lotSplitArray[1] + ' ' + lotTitle;
    }

    // unit & building
    if (lotSplitArray[0] === 'unit') {
      lotTitle = (buildingPrefix || 'Building') + ' ' + lotSplitArray[1] + ' ' + lotTitle;
    }
  }

  return lotTitle;
};

export const initiateBuildingLevelSVG = ({ dispatch }: { dispatch: Dispatch }) => {
  const svg = document.querySelector('#site-map-building-svg svg') as SVGGraphicsElement;

  document.querySelector('#site-map-building-svg svg')?.removeAttribute('width');
  document.querySelector('#site-map-building-svg svg')?.removeAttribute('height');

  // TODO: check with Jeet/Darren
  document.querySelectorAll('[id^=unit]').forEach((domUnit) => {
    domUnit.addEventListener('click', () => {
      dispatch(setSelectedLotId(domUnit.id));
    });
    domUnit.addEventListener('mouseover', () => {
      domUnit.classList.add('hovered-unit');
    });
    domUnit.addEventListener('mouseout', () => {
      domUnit.classList.remove('hovered-unit');
    });

    const href = domUnit.querySelector('use')!.getAttribute('xlink:href')!;
    document.querySelector(href)?.querySelector('path')!.setAttribute('fill', 'green');
    document.querySelector(href)?.querySelector('path')!.setAttribute('stroke', '5px');
  });

  // get BBox - svg dimensions without white space
  const BBox = svg.getBBox();
  // fix the svg's aspect ratio, make sure it fills its container, and center it in the viewBox
  svg.setAttribute(
    'viewBox',
    `${BBox.x - 60} ${BBox.y - 60} ${BBox.width + 120} ${BBox.height + 120}`
  );

  svg.setAttribute('width', `${BBox.width + 120}`);
  svg.setAttribute('height', `${BBox.height + 120}`);

  return document.querySelector('#site-map-building-svg svg')?.clientWidth;
};

export const changeLevel = (level: number) => {
  const svg = document.querySelector('#site-map-building-svg svg')!;

  Array.from(svg.children).forEach((c) => {
    if (c.id !== `level_${level + 1}`) {
      (c as HTMLElement).style.display = 'none';
    } else {
      (c as HTMLElement).style.display = '';
    }
  });
};

// Calculate the Ground Overlay image size
export const calcStaticMapSize = (data: ISmGroundOverlay) => {
  const swLat = data.smOverlaySouthWestCoordinates.split(',')[0];
  const neLat = data.smOverlayNorthEastCoordinates.split(',')[0];
  const adminZoom = data.adminSitePlanZoomLevel;

  const sitePlanKmWidth = Math.abs(Number(neLat) - Number(swLat)) * LAT_DEGREE;
  const kmPerPixel = kmPerPixelAtZoom(swLat, adminZoom);

  const sitePlanPixelWidth = sitePlanKmWidth / kmPerPixel;

  const sitePlanSvg = document.querySelector('#site-map-svg > svg') as Element;
  const sitePlanPixelWidthNew = sitePlanSvg?.getBoundingClientRect().width;

  return (sitePlanPixelWidthNew / sitePlanPixelWidth) * GOOGLE_MAP_PIXEL_WIDTH;
};

export const kmPerPixelAtZoom = (lat: string, zoom: number) => {
  return (156543.03392 * Math.cos((Number(lat) * Math.PI) / 180)) / Math.pow(2, zoom) / 1000;
};

export function getExternalAppURL({
  BASE_LAUNCH_URL,
  appCode,
  communityId,
  planCode,
}: {
  BASE_LAUNCH_URL: string;
  appCode: 'fp';
  communityId: string;
  planCode: string;
}): string;

export function getExternalAppURL({
  BASE_LAUNCH_URL,
  appCode,
  communityId,
}: {
  BASE_LAUNCH_URL: string;
  appCode: 'sp' | 'vm' | 'ki';
  communityId: string;
}): string;

export function getExternalAppURL({
  BASE_LAUNCH_URL,
  appCode,
  communityId,
  planCode,
}: {
  BASE_LAUNCH_URL: string;
  appCode: string;
  communityId: string;
  planCode?: string;
}) {
  return `
  ${BASE_LAUNCH_URL}?app=${appCode}&communityId=${communityId}${
    appCode === 'fp' ? `&planCode=${planCode}` : ''
  }`;
}
